const MS_PER_DAY = 24 * 60 * 60 * 1000;
const EN_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// D1/SQLite datetime: "YYYY-MM-DD HH:MM:SS" (UTC, no timezone).
const D1_UTC_RE =
  /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/;

const fromUnixEpoch = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  // 10-digit values are seconds; 13-digit values are milliseconds.
  const ms = n < 1e12 ? n * 1000 : n;
  const date = new Date(ms);
  return Number.isFinite(date.getTime()) ? date : null;
};

export const parseMessageDate = (value) => {
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }
  if (value == null || value === "") return null;

  if (typeof value === "object") {
    const seconds = value.seconds ?? value._seconds;
    if (seconds != null) return fromUnixEpoch(seconds);
    if (value.createdAt != null) return parseMessageDate(value.createdAt);
    if (value.created_at != null) return parseMessageDate(value.created_at);
    return null;
  }

  if (typeof value === "number") {
    return fromUnixEpoch(value);
  }

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^-?\d+(\.\d+)?$/.test(raw)) {
    return fromUnixEpoch(raw);
  }

  const d1 = raw.match(D1_UTC_RE);
  if (d1) {
    const [, day, clock, fraction = "", zone] = d1;
    const iso = zone
      ? `${day}T${clock}${fraction}${zone}`
      : `${day}T${clock}${fraction}Z`;
    const date = new Date(iso);
    return Number.isFinite(date.getTime()) ? date : null;
  }

  const date = new Date(raw);
  return Number.isFinite(date.getTime()) ? date : null;
};

export const getMessageTimestamp = (message) => {
  if (message == null) return null;
  if (typeof message !== "object") return message;
  return (
    message.createdAt ??
    message.created_at ??
    message.timestamp ??
    message.sentAt ??
    null
  );
};

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const calendarDayDiff = (date, now) =>
  Math.round((startOfDay(now) - startOfDay(date)) / MS_PER_DAY);

const formatClock = (date, isEn) =>
  date.toLocaleTimeString(isEn ? "en-US" : "zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
  });

const formatDatePart = (date, now, isEn) => {
  const sameYear = date.getFullYear() === now.getFullYear();
  if (isEn) {
    const month = EN_MONTHS[date.getMonth()];
    return sameYear
      ? `${month} ${date.getDate()}`
      : `${month} ${date.getDate()}, ${date.getFullYear()}`;
  }
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return sameYear
    ? `${month}月${day}日`
    : `${date.getFullYear()}年${month}月${day}日`;
};

export const isSameCalendarDay = (left, right) => {
  const a = parseMessageDate(left);
  const b = parseMessageDate(right);
  if (!a || !b) return false;
  return startOfDay(a) === startOfDay(b);
};

export const formatMessageDayLabel = (
  value,
  { isEn = false, now = new Date() } = {}
) => {
  const date = parseMessageDate(value);
  if (!date) return "";
  const diff = calendarDayDiff(date, now);
  if (diff === 0) return isEn ? "Today" : "今天";
  if (diff === 1) return isEn ? "Yesterday" : "昨天";
  return formatDatePart(date, now, isEn);
};

export const formatMessageTime = (
  value,
  { isEn = false, now = new Date() } = {}
) => {
  const date = parseMessageDate(value);
  if (!date) return "";
  const time = formatClock(date, isEn);
  const diff = calendarDayDiff(date, now);
  if (diff === 0) return time;
  if (diff === 1) return `${isEn ? "Yesterday" : "昨天"} ${time}`;
  return `${formatDatePart(date, now, isEn)} ${time}`;
};

export const isEnglishPath = (pathname = "") =>
  String(pathname || "").includes("/en/");
